import { zodResolver } from "@hookform/resolvers/zod";
import { MenuItem, Typography, Button } from "@mui/material";
import { ReactElement } from "react";
import { useForm } from "react-hook-form";
import z from "zod";

import { AudienceContactTypeTypeDefinitions } from "@stw/audience-contact-fields";
import { STATES } from "@stw/states";

import { FlexBox } from "~frontend/components/FlexBox";
import {
  RhfSelect,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import theme from "~frontend/theme";

import { CampaignForEditor } from "../../types";

const ZipArraySchema = z.array(
  AudienceContactTypeTypeDefinitions.ZIP.valueSchema()
);

const LocationFilterSchema = z.object({
  filterSubscribers: z.boolean(),
  combineWith: z.enum(["include", "exclude"]),
  states: z.array(AudienceContactTypeTypeDefinitions.STATE.valueSchema()),
  zips: z.string().refine(v => {
    const parseResult = ZipArraySchema.safeParse(v.split(","));

    return parseResult.success;
  }, "Must be a comma-separated list of five-digit ZIP codes"),
});

export default function LegacySubscriberImport({
  campaign: _campaign, // TODO: use default values from campaign's previous job args if they exist
}: {
  campaign: CampaignForEditor;
}): ReactElement {
  const defaultValues: {
    filterSubscribers: boolean;
    combineWith: "include" | "exclude";
    zips: string;
    states: string[];
  } = {
    filterSubscribers: false,
    combineWith: "include",
    zips: "",
    states: [],
  };

  const { control, watch } = useForm({
    resolver: zodResolver(LocationFilterSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues,
  });

  const filterSubscribers = watch("filterSubscribers");

  return (
    <form>
      {filterSubscribers ? (
        <>
          <FlexBox
            alignItems="flex-end"
            sx={{ marginBottom: theme.spacing(2) }}
          >
            <RhfSelect secondary name="combineWith" control={control}>
              <MenuItem value={"include"}>Include</MenuItem>
              <MenuItem value={"exclude"}>Exclude</MenuItem>
            </RhfSelect>
            <Typography>subscribers within the following locations:</Typography>
          </FlexBox>

          <RhfSelect
            label="States"
            fullWidth
            control={control}
            name="states"
            multiple
            bottomLabel="You may select multiple states."
          >
            {STATES.map(state => (
              <MenuItem key={state.abbreviation} value={state.abbreviation}>
                {state.name}
              </MenuItem>
            ))}
          </RhfSelect>

          <RhfTextField
            fullWidth
            name={"zips"}
            control={control}
            label="Zip codes"
            bottomLabel="You may enter multiple zip codes separated by commas."
          />
        </>
      ) : undefined}

      <FlexBox justifyContent="flex-end" gap={1}>
        {filterSubscribers ? (
          <Button color="primary" disabled>
            Clear filters
          </Button>
        ) : (
          <Button color="primary" disabled>
            Filter subscribers
          </Button>
        )}

        <Button color="primary" variant="contained" disabled>
          Import {filterSubscribers ? "Filtered" : "All"} Short Code Subscribers
        </Button>
      </FlexBox>
    </form>
  );
}
